import { useCallback } from "react";
import { urlParams } from "../constants/AppConstants";
import useRecordService from "../pages/recordManagement/useRecordService";

const CRM_CONTACT_SEARCH_FIRST_PAGE_INDEX = 0;
const CRM_CONTACT_SEARCH_PAGE_SIZE = 50;
const CRM_CONTACT_SEARCH_SORT_CREATED_DESC = "created_at:desc";

const parseApiData = (response) => {
    if (!response) return null;
    try {
        const parsed = JSON.parse(response);
        const data = parsed?.data?.data ?? parsed?.data;
        return typeof data === "string" ? JSON.parse(data) : data;
    } catch {
        return null;
    }
};

const PHONE_FIELD_NAMES = ["phone"];

/** CRM search rows use `values[]`; Teams channel API uses flat id/name/phone. */
const mapToRecipientOption = (source) => {
    if (!source) {
        return {
            fieldApiName: "phone",
            fieldName: "",
            fieldValue: "",
            fieldType: "phone",
            disabled: true,
        };
    }

    let id;
    let firstName = "";
    let lastName = "";
    let rawPhone = "";

    if (source && Array.isArray(source.values)) {
        const valuesMap = {};
        source.values.forEach((item) => {
            if (item?.key != null) valuesMap[item.key] = item.value;
        });
        firstName = valuesMap.first_name || valuesMap.firstName || "";
        lastName = valuesMap.last_name || valuesMap.lastName || "";
        for (const phoneFieldKey of PHONE_FIELD_NAMES) {
            if (valuesMap[phoneFieldKey]) {
                rawPhone = String(valuesMap[phoneFieldKey]);
                break;
            }
        }
        id = source.id;
    } else if (source) {
        id = source.id;
        firstName = source.firstName ?? "";
        lastName = source.lastName ?? "";
        rawPhone = source.phone != null ? String(source.phone) : "";
    }

    const phoneTrimmed = rawPhone.trim();
    const hasPhone = Boolean(phoneTrimmed);
    return {
        fieldApiName: "phone",
        fieldName: [firstName, lastName].filter(Boolean).join(" ") || phoneTrimmed || String(id ?? ""),
        fieldValue: phoneTrimmed,
        fieldType: "phone",
        id,
        disabled: !hasPhone,
    };
};

export default function useContactRecipients(appProperties, fetchData) {
    const controller = appProperties?.controller;
    const { searchRecords } = useRecordService();

    const fetchChannelRecipients = useCallback(
        async (queryParamsObj) => {
            const leftServiceId = appProperties?.leftServiceId || urlParams.get("leftServiceId");
            if (!leftServiceId) return [];
            const params = new URLSearchParams({ leftServiceId, ...queryParamsObj });
            const response = await fetchData(`teams/channel/recipients?${params}`, "GET", null, appProperties);
            const data = parseApiData(response);
            if (!data) return [];
            const recipientsList = Array.isArray(data) ? data : [];
            return recipientsList.map((channelRecipient) => mapToRecipientOption(channelRecipient));
        },
        [appProperties?.osyncId, fetchData]
    );

    const searchCrmContacts = useCallback(
        async (searchText, signal = null) => {
            if (!searchText?.trim()) return [];
            try {
                const payload = { moduleName: "Contacts", searchText: searchText.trim(), onlyDefault: false };
                const response = await searchRecords(
                    payload,
                    CRM_CONTACT_SEARCH_FIRST_PAGE_INDEX,
                    CRM_CONTACT_SEARCH_PAGE_SIZE,
                    CRM_CONTACT_SEARCH_SORT_CREATED_DESC,
                    signal
                );
                const content = response?.data?.content || [];
                return content.map((crmRecord) => mapToRecipientOption(crmRecord));
            } catch (err) {
                if (err?.code !== "ERR_CANCELED") {
                    console.error("CRM search failed:", err);
                }
                return [];
            }
        },
        [searchRecords]
    );

    const getQueryParams = (context) => {
        if (context?.channel?.id) {
            return { conversationId: context?.channel?.id, sourceModule: "channel" };
        }
        if (context?.chat?.id) {
            return { conversationId: context?.chat?.id, sourceModule: "chat" };
        }
        return {};
    };

    const loadRecipients = useCallback(
        (setToPhoneNumberList, setToNumber, setLoading) => {
            if (!controller || controller?.service !== "Teams") {
                setLoading?.(false);
                return Promise.resolve();
            }
            setLoading?.(true);
            return controller
                .getContext()
                .then(async (teamsHostContext) => {
                    console.warn("teamsHostContext", teamsHostContext);
                    const queryParamsObj = getQueryParams(teamsHostContext);
                    return fetchChannelRecipients(queryParamsObj);
                })
                .then((list) => {
                    setToPhoneNumberList?.(list || []);
                })
                .catch(() => setToPhoneNumberList?.([]))
                .finally(() => setLoading?.(false));
        },
        [controller?.service, fetchChannelRecipients]
    );

    return { loadRecipients, searchCrmContacts };
}
